'use client'

import { useEffect, useState, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import Link from 'next/link'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Plus, ShoppingBag, Trash2, ArrowRight, Wheat, ChevronDown, History, ShoppingCart, FileText, Flame, Gift, MessageSquare, CakeSlice } from 'lucide-react'
import { useCartStore } from '@/store/cartStore'
import { cartItemHref } from '@/lib/cartLinks'
import { formatPrice, parseCustomizations } from '@/lib/utils'
import {
  WAARDEBON_PRODUCT_ID, WAARDEBON_WRAP_FROM, isReorderable, CUSTOM_CAKE_LEAD_DAYS,
  slicingCustomization, isSlicingCustomization, withSlicedKey, DEFAULT_SLICING_PRICE,
  SLICING_GROUP_ID, SLICING_OPTION_ID,
} from '@/lib/bakery'
import toast from 'react-hot-toast'
import { useSession } from 'next-auth/react'
import QuickAddModal from '@/components/products/QuickAddModal'
import QuantityStepper from '@/components/ui/QuantityStepper'
import BulkOrderNotice from '@/components/ui/BulkOrderNotice'

/* ─── Types ─────────────────────────────────────────────────────────────── */
export interface PrevOrderItem {
  id:        string
  productId: string
  name:      string
  price:     number
  quantity:  number
  image:     string
  /** JSON van de gekozen opties bij die bestelling, inclusief "laten snijden". */
  customizations?: string | null
  product:   { slug: string; images: string; name: string; canBeSliced?: boolean; minLeadDays?: number | null } | null
}

interface PrevOrder {
  id:          string
  orderNumber: string
  createdAt:   string
  items:       PrevOrderItem[]
}

/** Zet een regel uit een eerdere bestelling om naar een winkelwagen-regel.
 *  Gedeeld met /account, waar dezelfde "opnieuw bestellen"-knop staat.
 *
 *  De gemaakte keuzes gaan mee terug: gekozen opties én "laten snijden" staan
 *  weer precies zoals de klant ze de vorige keer had. Twee dingen om te weten:
 *  - `OrderItem.price` is de stukprijs INCLUSIEF de toeslagen van die opties,
 *    dus die trekken we er weer af — anders telt de winkelwagen ze dubbel.
 *  - Een bestelregel bewaart alleen namen, geen optie-ids. De cartKey bouwen
 *    we daarom uit die namen; producten zonder opties (het gros, en alles wat
 *    alleen gesneden wordt) krijgen exact dezelfde sleutel als vanaf de
 *    productpagina, zodat de regels netjes samenvallen. */
export function prevOrderItemToCartItem(
  item: Pick<PrevOrderItem, 'productId' | 'name' | 'price' | 'image' | 'product' | 'customizations'>,
): Omit<import('@/store/cartStore').CartItem, 'quantity'> {
  let image = ''
  if (item.product?.images) {
    try { image = JSON.parse(item.product.images)?.[0] ?? '' } catch { image = '' }
  }
  if (!image) image = item.image ?? ''

  const stored  = parseCustomizations(item.customizations)
  const sliced  = stored.find(isSlicingCustomization)
  const options = stored.filter((c) => !isSlicingCustomization(c))

  const customizations = stored.map((c) => ({
    groupId:    isSlicingCustomization(c) ? SLICING_GROUP_ID : c.groupName,
    groupName:  c.groupName,
    optionId:   isSlicingCustomization(c) ? SLICING_OPTION_ID : c.optionName,
    optionName: c.optionName,
    priceAddon: c.priceAddon ?? 0,
  }))

  const addons    = customizations.reduce((sum, c) => sum + c.priceAddon, 0)
  const basePrice = Math.max(0, Math.round((item.price - addons) * 100) / 100)

  const optionKey = options.map((c) => c.optionName).sort().join(':')
  const baseKey   = optionKey ? `${item.productId}:${optionKey}` : item.productId

  return {
    cartKey:   withSlicedKey(baseKey, !!sliced),
    productId: item.productId,
    name:      item.name,
    price:     basePrice,
    salePrice: null,
    image,
    slug:      item.product?.slug ?? '',
    customizations: customizations.length ? customizations : undefined,
    canBeSliced:  item.product?.canBeSliced || undefined,
    slicingPrice: sliced?.priceAddon,
    // Voorbereidingstijd mee terug, anders klapt het afrekenen pas bij het
    // versturen om over een taart die twee open dagen vooruit moet. De taarten
    // die wij op maat maken staan niet als Product in de database, dus daar
    // komt de voorbereidingstijd van de constante.
    minLeadDays:  item.product?.minLeadDays ?? CUSTOM_CAKE_LEAD_DAYS[item.productId ?? ''] ?? undefined,
  }
}

/* ─── CartItem row ──────────────────────────────────────────────────────── */
function CartItemRow({
  item,
  onRemove,
  onQuantityChange,
  onClose,
}: {
  item: import('@/store/cartStore').CartItem
  onRemove:         (id: string) => void
  onQuantityChange: (id: string, qty: number) => void
  onClose:          () => void
}) {
  const updateItem = useCartStore((s) => s.updateItem)
  const effectivePrice  = item.salePrice ?? item.price

  /* Waardebon: vanaf de drempel wordt de bon standaard gratis feestelijk
     ingepakt. De keuze is ook hier in de winkelwagen zichtbaar én uit te
     zetten; wie 'm uitzet krijgt een cartKey op ':plain', zodat het effect
     hieronder de keuze niet meteen weer aanvinkt. */
  const isWaardebon   = item.productId === WAARDEBON_PRODUCT_ID
  const wrapAvailable = isWaardebon && effectivePrice >= WAARDEBON_WRAP_FROM
  const isWrapped     = (item.customizations ?? []).some((c) => c.groupId === 'waardebon-verpakking')

  const toggleWrap = () => {
    const base = (item.customizations ?? []).filter((c) => c.groupId !== 'waardebon-verpakking')
    const next = isWrapped
      ? base
      : [...base, {
          groupId:    'waardebon-verpakking',
          groupName:  'Verpakking',
          optionId:   'feestelijk',
          optionName: 'Feestelijk ingepakt (gratis)',
          priceAddon: 0,
        }]
    updateItem(item.cartKey, {
      customizations: next,
      cartKey: `${WAARDEBON_PRODUCT_ID}:${effectivePrice}:${isWrapped ? 'plain' : 'wrap'}`,
    })
  }
  /* Standaardkeuze: is de bon duur genoeg en heeft de klant het inpakken niet
     zelf uitgezet, dan vinken we het hier alsnog aan. Dat vangt bonnen op die
     via een ander pad (of vóór deze wijziging) in de wagen zijn gekomen. */
  const declinedWrap = item.cartKey.endsWith(':plain')
  useEffect(() => {
    if (wrapAvailable && !isWrapped && !declinedWrap) toggleWrap()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wrapAvailable, isWrapped, declinedWrap])

  /* Laten snijden: gratis, aan/uit te zetten zolang de bestelling in de
     winkelwagen staat. De regel krijgt een eigen cartKey, zodat dezelfde taart
     wel én niet gesneden naast elkaar kan staan. */
  const slicingAvailable = item.canBeSliced === true
  const slicingPrice     = item.slicingPrice ?? DEFAULT_SLICING_PRICE
  const isSliced         = (item.customizations ?? []).some(isSlicingCustomization)

  const toggleSliced = () => {
    const rest = (item.customizations ?? []).filter((c) => !isSlicingCustomization(c))
    updateItem(item.cartKey, {
      customizations: isSliced ? rest : [...rest, slicingCustomization(slicingPrice)],
      cartKey: withSlicedKey(item.cartKey, !isSliced),
    })
  }

  const totalAddonPerUnit = (item.customizations ?? []).reduce((sum, c) => sum + (c.priceAddon ?? 0), 0)
  const effectiveWithAddon = effectivePrice + totalAddonPerUnit
  const lineTotal          = effectiveWithAddon * item.quantity
  const hasAddons          = totalAddonPerUnit > 0

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0  }}
      exit={{ opacity: 0, x: 30, transition: { duration: 0.18 } }}
      className="flex flex-col py-3 px-2 rounded-xl transition-colors hover:bg-[#8B4513]/5"
    >
      {/* Row: thumbnail, then everything else. The name spans the full
          remaining width and stops only at the bin — the price and stepper sit
          on their own line underneath, so a long product name is no longer
          squeezed into half a column. */}
      <div className="flex gap-3">
        <Link href={cartItemHref(item)} onClick={onClose} className="shrink-0" tabIndex={-1} aria-hidden="true">
          <div className="w-16 h-16 rounded-xl overflow-hidden bg-[#F4A460]/10 relative">
            {item.image ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={item.image}
                alt={item.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[#D2691E]/40">
                <Wheat size={24} />
              </div>
            )}
          </div>
        </Link>

        <div className="flex-1 min-w-0">
          {/* Title + unit price, with the remove button as the only thing
              claiming width beside them. */}
          <div className="flex items-start gap-2">
            <Link href={cartItemHref(item)} onClick={onClose} className="flex-1 min-w-0 group/name">
              <p
                className="font-semibold text-[#2C1810] text-sm leading-snug line-clamp-2 group-hover/name:text-[#8B4513] transition-colors"
                title={item.name}
              >
                {item.name}
              </p>
              <p className="text-xs text-[#8B4513]/60 mt-0.5">
                {item.salePrice != null && (
                  <span className="line-through mr-1.5 text-[#8B4513]/40">
                    {formatPrice(item.price)}
                  </span>
                )}
                <span className={item.salePrice != null ? 'text-[#D2691E] font-semibold' : ''}>
                  {formatPrice(effectivePrice)}
                </span>
              </p>
            </Link>
            <button
              onClick={() => onRemove(item.cartKey)}
              className="shrink-0 -mt-0.5 p-1 rounded-md text-[#8B4513]/40 hover:text-red-500 hover:bg-red-50 transition-colors"
              aria-label={`${item.name} verwijderen`}
            >
              <Trash2 size={14} />
            </button>
          </div>

          {/* Bundle contents: every included item, one per line */}
          {item.bundleContents && item.bundleContents.length > 0 && (
            <ul className="mt-1 text-[10px] leading-relaxed text-[#8B4513]/60">
              {item.bundleContents.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
          )}

          {/* Customizations */}
          {item.customizations && item.customizations.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {item.customizations.map((c, i) => (
                <span
                  key={i}
                  className="text-[10px] px-1.5 py-0.5 bg-[#8B4513]/10 text-[#8B4513] rounded-full whitespace-nowrap"
                >
                  {c.groupName}: {c.optionName}
                  {c.priceAddon !== 0 && ` +€${c.priceAddon.toFixed(2).replace('.', ',')}`}
                </span>
              ))}
            </div>
          )}

          {/* Fototaart details */}
          {item.fototaartData && (
            <div className="mt-1 text-[10px] text-[#8B4513]/70 space-y-0.5">
              {item.fototaartData.text && (
                <p className="flex items-center gap-1"><FileText size={10} />&ldquo;{item.fototaartData.text}&rdquo;</p>
              )}
              {item.fototaartData.candles > 0 && (
                <p className="flex items-center gap-1"><Flame size={10} />{item.fototaartData.candles} kaarsjes</p>
              )}
              {item.fototaartData.notes && (
                <p className="flex items-start gap-1"><MessageSquare size={10} className="mt-0.5 shrink-0" />{item.fototaartData.notes}</p>
              )}
            </div>
          )}

          {/* Stepper and line total share the bottom line */}
          <div className="mt-2 flex items-end justify-between gap-2">
            <QuantityStepper
              value={item.quantity}
              onChange={(q) => onQuantityChange(item.cartKey, q)}
              min={item.minQty ?? 1}
              max={item.maxOnlineQty ?? 9999}
              size="sm"
            />
            {hasAddons ? (
              <div className="text-right space-y-0.5">
                <p className="text-[10px] text-[#8B4513]/50">{item.quantity}× {formatPrice(effectivePrice)}</p>
                <p className="text-[10px] text-[#D2691E]/70">+{item.quantity}× {formatPrice(totalAddonPerUnit)}</p>
                <p className="font-bold text-sm text-[#2C1810]">{formatPrice(lineTotal)}</p>
              </div>
            ) : (
              <p className="font-bold text-sm text-[#2C1810]">{formatPrice(lineTotal)}</p>
            )}
          </div>
        </div>
      </div>

      {/* Waardebon: feestelijke verpakking (gratis, standaard aan vanaf de drempel) */}
      {wrapAvailable && (
        <label
          className="mt-2 mx-2 flex items-center gap-2 px-2.5 py-2 rounded-lg cursor-pointer transition-colors"
          style={{
            background: isWrapped ? '#F0FDF4' : '#FEF3E8',
            border: isWrapped ? '1px solid #86EFAC' : '1px solid #F4A460',
          }}
        >
          <input
            type="checkbox"
            checked={isWrapped}
            onChange={toggleWrap}
            className="w-3.5 h-3.5 accent-[#16A34A]"
          />
          <Gift size={13} className={isWrapped ? 'text-green-600' : 'text-[#8B4513]'} />
          <span className="text-[11px] font-medium" style={{ color: isWrapped ? '#166534' : '#6B4226' }}>
            {isWrapped ? 'Wordt gratis feestelijk ingepakt' : 'Gratis feestelijk inpakken?'}
          </span>
        </label>
      )}

      {/* Laten snijden (gratis) — snel aan of uit te zetten */}
      {slicingAvailable && (
        <label
          className="mt-2 mx-2 flex items-center gap-2 px-2.5 py-2 rounded-lg cursor-pointer transition-colors"
          style={{
            background: isSliced ? '#F0FDF4' : '#FEF3E8',
            border: isSliced ? '1px solid #86EFAC' : '1px solid #F4A460',
          }}
        >
          <input
            type="checkbox"
            checked={isSliced}
            onChange={toggleSliced}
            className="w-3.5 h-3.5 accent-[#16A34A]"
          />
          <CakeSlice size={13} className={isSliced ? 'text-green-600' : 'text-[#8B4513]'} />
          <span className="text-[11px] font-medium" style={{ color: isSliced ? '#166534' : '#6B4226' }}>
            {isSliced
              ? 'Wordt in punten gesneden'
              : `Laten snijden?${slicingPrice > 0 ? ` (+${formatPrice(slicingPrice)})` : ' (gratis)'}`}
          </span>
        </label>
      )}

      {/* Minimum-quantity note (seizoen rule) */}
      {item.minQty != null && item.minQty > 1 && (
        <p className="mt-1.5 px-2 text-[10px] text-[#8B4513]/60">
          Minimale afname: {item.minQty} stuks
        </p>
      )}

      {/* "Max online" notice for this line */}
      {item.maxOnlineQty != null && item.quantity >= item.maxOnlineQty && (
        <div className="mt-2 px-2">
          <BulkOrderNotice
            quantity={item.quantity}
            maxOnlineQty={item.maxOnlineQty}
            size="sm"
          />
        </div>
      )}
    </motion.div>
  )
}

/* ─── Previous Order Box ─────────────────────────────────────────────────── */
function PreviousOrderBox({ isOpen: sidebarOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { status }  = useSession()
  const addItem     = useCartStore((s) => s.addItem)

  const [open,    setOpen]    = useState(false)
  const [order,   setOrder]   = useState<PrevOrder | null>(null)
  const [loading, setLoading] = useState(false)
  const [fetched, setFetched] = useState(false)

  const fetchOrder = useCallback(async () => {
    if (fetched || status !== 'authenticated') return
    setLoading(true)
    try {
      const res  = await fetch('/api/orders?limit=1')
      const data = await res.json()
      const prev = data.orders?.[0] ?? null
      setOrder(prev)
    } catch {
      // silently ignore
    } finally {
      setLoading(false)
      setFetched(true)
    }
  }, [fetched, status])

  /* Fetch when sidebar opens and user is logged in */
  useEffect(() => {
    if (sidebarOpen && status === 'authenticated' && !fetched) {
      fetchOrder()
    }
  }, [sidebarOpen, status, fetched, fetchOrder])

  if (status !== 'authenticated') return null

  /* Toevoegen vanuit de winkelwagen zelf: `silent`, want de klant kijkt al naar
     de wagen — het meldingskaartje onder de navbar zou dubbelop zijn. */
  const handleAddOne = (item: PrevOrderItem, qty = 1) => {
    addItem(prevOrderItemToCartItem(item), qty, { silent: true })
  }

  const handleAddAll = () => {
    if (!order) return
    let skipped = 0
    for (const item of order.items) {
      if (!isReorderable(item.productId)) { skipped++; continue }
      handleAddOne(item, item.quantity)
    }
    if (skipped > 0) {
      toast('Een taart met foto of tekst en een waardebon stel je opnieuw samen. Die staan er dus niet bij.', { icon: 'ℹ️' })
    }
  }

  return (
    <div className="border-b border-[#D2691E]/20 bg-[#FDF6EC]">
      {/* Accordion header */}
      <button
        onClick={() => {
          setOpen((v) => !v)
          if (!fetched) fetchOrder()
        }}
        className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-[#8B4513]/5 transition-colors"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2">
          <History size={16} className="text-[#8B4513] shrink-0" />
          <span className="text-sm font-semibold text-[#2C1810]">Laatste bestelling</span>
        </div>
        <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2 }}>
          <ChevronDown size={16} className="text-[#8B4513]" />
        </motion.div>
      </button>

      {/* Accordion body */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="prev-order-body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            style={{ overflow: 'hidden' }}
          >
            <div className="border-t border-[#D2691E]/15 px-5 pt-3 pb-4">
              {loading && (
                <p className="text-xs text-[#8B4513]/60 text-center py-3">Laden...</p>
              )}

              {!loading && !order && (
                <p className="text-xs text-[#8B4513]/60 text-center py-3">
                  Nog geen bestellingen gevonden
                </p>
              )}

              {!loading && order && (
                <>
                  {/* Item list */}
                  <ul className="space-y-2 mb-3 max-h-52 overflow-y-auto pr-3">
                    {order.items.map((item) => {
                      const images = item.product?.images
                      const image  = images ? (JSON.parse(images)?.[0] ?? '') : ''
                      return (
                        <li key={item.id} className="flex items-center gap-2">
                          {/* Thumbnail */}
                          <div className="w-10 h-10 rounded-lg overflow-hidden shrink-0 bg-[#F4A460]/10">
                            {image ? (
                              // eslint-disable-next-line @next/next/no-img-element
                              <img
                                src={image}
                                alt={item.name}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-[#D2691E]/30">
                                <Wheat size={16} />
                              </div>
                            )}
                          </div>

                          {/* Name + qty */}
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-[#2C1810] line-clamp-2 leading-snug" title={item.name}>{item.name}</p>
                            <p className="text-xs text-[#8B4513]/50">×{item.quantity} · {formatPrice(item.price)}</p>
                          </div>

                          {/* Add button — configureerbare producten (fototaart,
                              teksttaart, waardebon) stel je opnieuw samen. */}
                          {isReorderable(item.productId) ? (
                            <button
                              onClick={() => handleAddOne(item)}
                              className="shrink-0 w-7 h-7 flex items-center justify-center rounded-lg bg-[#8B4513]/10 hover:bg-[#8B4513]/20 text-[#8B4513] transition-colors"
                              aria-label={`${item.name} toevoegen`}
                            >
                              <Plus size={13} />
                            </button>
                          ) : (
                            <Link
                              href={item.product?.slug ? `/winkel/product/${item.product.slug}` : '/winkel'}
                              onClick={onClose}
                              className="shrink-0 px-2 h-7 flex items-center rounded-lg bg-[#8B4513]/10 hover:bg-[#8B4513]/20 text-[#8B4513] text-[10px] font-semibold transition-colors"
                              title="Dit product stel je opnieuw samen"
                            >
                              Samenstellen
                            </Link>
                          )}
                        </li>
                      )
                    })}
                  </ul>

                  {/* Add entire order */}
                  <button
                    onClick={handleAddAll}
                    className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-colors"
                    style={{ background: '#8B4513', color: '#FDF6EC' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = '#D2691E')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = '#8B4513')}
                  >
                    <ShoppingCart size={13} />
                    Hele bestelling toevoegen
                  </button>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

/* ─── Upsell suggestions ────────────────────────────────────────────────── */
interface SuggestionOption { id: string; name: string; priceAddon: number; isDefault: boolean }
interface SuggestionGroup  { id: string; name: string; isRequired: boolean; options: SuggestionOption[] }
interface SuggestionProduct {
  id: string; name: string; slug: string; price: number; salePrice: number | null
  images: string; optionGroups?: SuggestionGroup[]
  bulkMaxOnlineQty?: number | null
  orderMinQty?: number | null
  outOfSeasonOrder?: boolean
  minLeadDays?: number | null
}

function firstImage(images: string): string {
  try {
    const arr = JSON.parse(images)
    if (Array.isArray(arr) && arr[0]) return arr[0]
  } catch { if (images?.startsWith('/') || images?.startsWith('http')) return images }
  return '/images/placeholder-product.svg'
}

function SuggestionsRow({ isOpen: sidebarOpen, excludeIds }: { isOpen: boolean; excludeIds: string[] }) {
  const addItem = useCartStore((s) => s.addItem)
  const [products, setProducts] = useState<SuggestionProduct[]>([])
  const [mounted, setMounted]   = useState(false)
  const [chooser, setChooser]   = useState<SuggestionProduct | null>(null)
  const fetchedRef = useRef(false)

  useEffect(() => { setMounted(true) }, [])

  useEffect(() => {
    if (!sidebarOpen || fetchedRef.current) return
    fetchedRef.current = true
    fetch('/api/products?sort=popular&limit=12')
      .then((r) => (r.ok ? r.json() : { products: [] }))
      .then((d) => setProducts(Array.isArray(d.products) ? d.products : []))
      .catch(() => {})
  }, [sidebarOpen])

  const suggestions = products.filter((p) => !excludeIds.includes(p.id)).slice(0, 6)
  if (suggestions.length === 0) return null

  const hasOptions = (p: SuggestionProduct) => (p.optionGroups ?? []).some((g) => g.options.length > 0)

  /* Direct add for simple products; option products open the chooser so the
     buyer picks the variant (Half/Heel brood, taart-grootte, …) — same modal
     as the shop grid. */
  const handleAdd = (p: SuggestionProduct) => {
    if (hasOptions(p)) { setChooser(p); return }
    addItem({
      cartKey:   p.id,
      productId: p.id,
      name:      p.name,
      price:     p.price,
      salePrice: p.salePrice ?? null,
      image:     firstImage(p.images),
      slug:      p.slug,
      maxOnlineQty: p.bulkMaxOnlineQty ?? undefined,
      minQty:       (p.orderMinQty ?? 1) > 1 ? p.orderMinQty ?? undefined : undefined,
      minLeadDays:  p.minLeadDays ?? undefined,
    }, Math.max(1, p.orderMinQty ?? 1))
  }

  return (
    <div className="border-t border-[#D2691E]/20 px-5 pt-3 pb-1 shrink-0">
      <p className="text-xs font-bold text-[#8B4513] uppercase tracking-wide mb-2.5">
        Maak je bestelling compleet
      </p>
      <div className="flex gap-3 overflow-x-auto pb-3 -mx-1 px-1" style={{ scrollbarWidth: 'thin' }}>
        {suggestions.map((p) => {
          const price = p.salePrice ?? p.price
          return (
            <div key={p.id} className="shrink-0 w-28 flex flex-col">
              <Link href={`/winkel/product/${p.slug}`} className="block group">
                <div className="relative w-28 h-28 rounded-xl overflow-hidden bg-[#F4A460]/10 border border-[#D2691E]/15">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={firstImage(p.images)}
                    alt={p.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    onError={(e) => { (e.target as HTMLImageElement).src = '/images/placeholder-product.svg' }}
                  />
                </div>
                {/* Fixed 2-line height so the price + button row aligns across cards */}
                <p
                  className="text-xs font-semibold text-[#2C1810] leading-tight mt-1.5 line-clamp-2 group-hover:text-[#8B4513] transition-colors"
                  style={{ minHeight: '2rem' }}
                >
                  {p.name}
                </p>
              </Link>
              <div className="flex items-center justify-between mt-auto pt-1">
                <span className="text-xs font-bold text-[#8B4513]">
                  {hasOptions(p) ? `v.a. ${formatPrice(price)}` : formatPrice(price)}
                </span>
                <button
                  onClick={() => handleAdd(p)}
                  className="shrink-0 w-7 h-7 flex items-center justify-center rounded-lg bg-[#8B4513]/10 hover:bg-[#8B4513] text-[#8B4513] hover:text-[#FDF6EC] transition-colors active:scale-90"
                  aria-label={hasOptions(p) ? `${p.name}, kies opties` : `${p.name} toevoegen`}
                >
                  <Plus size={14} />
                </button>
              </div>
            </div>
          )
        })}
      </div>

      {/* Option chooser (portal so it layers above the cart drawer) */}
      {mounted && createPortal(
        <AnimatePresence>
          {chooser && (
            <QuickAddModal
              product={chooser}
              mainImage={firstImage(chooser.images)}
              activePrice={chooser.salePrice ?? chooser.price}
              isOnSale={chooser.salePrice != null && chooser.salePrice < chooser.price}
              onClose={() => setChooser(null)}
            />
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  )
}

/* ─── Empty state ───────────────────────────────────────────────────────── */
function EmptyCart({ onClose }: { onClose: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center flex-1 px-6 py-12 text-center">
      <div className="w-20 h-20 rounded-full bg-[#8B4513]/8 flex items-center justify-center mb-5">
        <ShoppingBag size={32} className="text-[#D2691E]/60" />
      </div>
      <h3 className="font-playfair font-bold text-[#2C1810] text-xl mb-2">
        Uw winkelwagen is leeg
      </h3>
      <p className="text-sm text-[#8B4513]/60 leading-relaxed mb-6">
        Ontdek onze verse producten en voeg uw favorieten toe aan de winkelwagen.
      </p>
      <Link
        href="/winkel"
        onClick={onClose}
        className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-colors"
        style={{ background: '#8B4513', color: '#FDF6EC' }}
        onMouseEnter={(e) => (e.currentTarget.style.background = '#D2691E')}
        onMouseLeave={(e) => (e.currentTarget.style.background = '#8B4513')}
      >
        <Wheat size={16} />
        Naar de winkel
      </Link>
    </div>
  )
}

/* ─── CartSidebar ───────────────────────────────────────────────────────── */
export default function CartSidebar() {
  const {
    items,
    isOpen,
    closeCart,
    removeItem,
    updateQuantity,
    totalItems,
    totalPrice,
  } = useCartStore()

  /* Prevent body scroll when cart is open */
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [isOpen])

  /* Close on Escape */
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') closeCart() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [closeCart])

  const count = totalItems()
  const total = totalPrice()

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            key="cart-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22 }}
            className="fixed inset-0 z-[150] bg-black/50 backdrop-blur-sm"
            onClick={closeCart}
            aria-hidden="true"
          />

          {/* Sidebar panel */}
          <motion.aside
            key="cart-sidebar"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            className="fixed top-0 right-0 bottom-0 z-[160] w-full max-w-sm bg-[#FDF6EC] flex flex-col shadow-[0_0_50px_rgba(44,24,16,0.25)]"
            aria-label="Winkelwagen"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#D2691E]/20 shrink-0">
              <div className="flex items-center gap-2">
                <ShoppingBag size={20} className="text-[#8B4513]" />
                <h2 className="font-playfair font-bold text-[#2C1810] text-lg">
                  Winkelwagen
                  {count > 0 && (
                    <span className="ml-2 text-sm font-semibold text-[#8B4513]/60">
                      ({count} {count === 1 ? 'item' : 'items'})
                    </span>
                  )}
                </h2>
              </div>
              <button
                onClick={closeCart}
                className="p-2 rounded-lg text-[#8B4513] hover:bg-[#8B4513]/10 transition-colors"
                aria-label="Winkelwagen sluiten"
              >
                <X size={20} />
              </button>
            </div>

            {/* Laatste bestelling — always visible right below header */}
            <PreviousOrderBox isOpen={isOpen} onClose={closeCart} />

            {/* Content */}
            {items.length === 0 ? (
              <EmptyCart onClose={closeCart} />
            ) : (
              <>
                {/* Items list */}
                <div className="flex-1 overflow-y-auto px-5">
                  <AnimatePresence initial={false}>
                    {items.map((item) => (
                      <div key={item.cartKey} className="border-b border-[#D2691E]/10 last:border-0">
                        <CartItemRow
                          item={item}
                          onRemove={removeItem}
                          onQuantityChange={updateQuantity}
                          onClose={closeCart}
                        />
                      </div>
                    ))}
                  </AnimatePresence>
                </div>

                {/* Upsell suggestions — right above the totals/checkout */}
                <SuggestionsRow isOpen={isOpen} excludeIds={items.map((i) => i.productId)} />

                {/* Footer */}
                <div className="border-t border-[#D2691E]/20 px-5 py-5 space-y-4 shrink-0">
                  {/* Subtotal */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-[#8B4513]/70">Subtotaal</span>
                    <span className="font-bold text-[#2C1810] text-lg">{formatPrice(total)}</span>
                  </div>

                  {/* Shipping note */}
                  <p className="text-xs text-[#8B4513]/50 text-center bg-[#8B4513]/5 rounded-lg px-4 py-2">
                    Gratis afhalen in de winkel. Bezorging in Rijssen € 4,95, regio € 15,00.
                  </p>

                  {/* Checkout button */}
                  <Link
                    href="/afrekenen"
                    onClick={closeCart}
                    className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl font-bold text-sm transition-colors"
                    style={{ background: '#8B4513', color: '#FDF6EC' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = '#D2691E')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = '#8B4513')}
                  >
                    Naar afrekenen
                    <ArrowRight size={16} />
                  </Link>

                  {/* Continue shopping */}
                  <button
                    onClick={closeCart}
                    className="w-full text-center text-sm text-[#8B4513] hover:text-[#D2691E] font-semibold transition-colors py-1"
                  >
                    Doorgaan met winkelen
                  </button>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  )
}
